/**
 * /reset/:token — set new password from a reset link.
 *
 * Mirrors InviteAcceptPage but for the reset flow:
 *   - GET /auth/password-reset/:token/preview to verify the token.
 *   - POST /auth/password-reset/:token { password } to consume.
 *   - On success → /login with 'Пароль изменён, войдите' toast.
 *
 * Layout: AuthLayout.
 */
import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { PasswordSchema } from '@aris/shared';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/password-input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { ApiError, apiFetch } from '@/lib/fetcher';

interface ResetPreview {
  email: string;
  expiresAt: string;
  valid: boolean;
}

const FormSchema = z
  .object({
    password: PasswordSchema,
    confirmPassword: z.string().min(1),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  });
type FormValues = z.infer<typeof FormSchema>;

export function ResetPasswordPage(): React.ReactElement {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [preview, setPreview] = React.useState<ResetPreview | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onSubmit',
  });

  React.useEffect(() => {
    const ac = new AbortController();
    setPreviewLoading(true);
    apiFetch<ResetPreview>(`/auth/password-reset/${encodeURIComponent(token)}/preview`, {
      signal: ac.signal,
    })
      .then((data) => {
        if (!data.valid) {
          setPreviewError('Ссылка для сброса недействительна или просрочена');
          return;
        }
        setPreview(data);
      })
      .catch((err) => {
        if ((err as { name?: string })?.name === 'AbortError') return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
          setPreviewError('Ссылка для сброса недействительна или просрочена');
        } else {
          setPreviewError('Не удалось проверить ссылку');
        }
      })
      .finally(() => setPreviewLoading(false));
    return () => ac.abort();
  }, [token]);

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await apiFetch<{ ok: true }>(`/auth/password-reset/${encodeURIComponent(token)}`, {
        method: 'POST',
        body: { password: values.password },
      });
      toast('Пароль изменён, войдите', { variant: 'success' });
      navigate('/login', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 410 || err.status === 404)) {
        setPreviewError('Ссылка уже использована или просрочена');
        return;
      }
      if (err instanceof ApiError && err.status === 422) {
        toast(err.message || 'Не удалось изменить пароль', { variant: 'error' });
        return;
      }
      toast('Не удалось изменить пароль', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  });

  if (previewLoading) {
    return (
      <div data-testid="reset-loading" className="space-y-4">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  if (previewError) {
    return (
      <div data-testid="reset-error" className="space-y-4 text-center">
        <h1 className="text-xl font-semibold text-ink">Ссылка недействительна</h1>
        <p className="text-sm text-muted-foreground">{previewError}</p>
        <Link
          to="/login"
          className="inline-block text-sm text-accent underline-offset-4 hover:underline"
        >
          Назад ко входу
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="reset-page" className="space-y-5">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold text-ink">Новый пароль</h1>
        {preview ? (
          <p className="text-sm text-muted-foreground">
            Установите новый пароль для {preview.email}
          </p>
        ) : null}
      </div>
      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <FormField
            name="password"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Пароль</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" autoFocus {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="confirmPassword"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Подтверждение пароля</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={submitting}
            data-testid="reset-submit"
          >
            {submitting ? 'Сохранение…' : 'Изменить пароль'}
          </Button>
        </form>
      </Form>
    </div>
  );
}
